import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from '../api/axios';
import AuthContext from '../context/AuthContext';
import SkeletonLoader from './SkeletonLoader';
import { 
    FaGraduationCap, FaPlus, FaKey, FaUsersCog, FaLink, 
    FaEdit, FaTrash, FaBuilding, FaUserTie, FaUserPlus 
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { BaseModal, SuccessModal, ErrorModal, ConfirmModal, LoadingModal } from './SharedComponents/Modals';

const CoursesModule = () => {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();

    // --- DATI ---
    const [courses, setCourses] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [availableClasses, setAvailableClasses] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- STATO MODALI ---
    // Gestione centralizzata: true = aperto, false = chiuso
    const [modals, setModals] = useState({
        form: false,
        assignClasses: false,
        assignInstructors: false,
        success: false,
        error: false,
        confirmDelete: false,
        loading: false
    });

    const [currentCourse, setCurrentCourse] = useState(null);
    const [formData, setFormData] = useState({ id: null, name: '', description: '', tenant_id: '' });
    const [selectedItems, setSelectedItems] = useState([]); 
    const [statusMsg, setStatusMsg] = useState('');

    const isSuperAdmin = auth.role === 'superadmin';
    const isAdmin = auth.role === 'admin';
    const isInstructor = auth.role === 'instructor';

    // --- FUNZIONE UTILITÀ PER CHIUDERE TUTTO PRIMA DI APRIRE ---
    const resetModals = () => {
        setModals({
            form: false,
            assignClasses: false,
            assignInstructors: false,
            success: false,
            error: false,
            confirmDelete: false,
            loading: false
        });
    };

    // --- FETCH DATI ---
    const fetchCourses = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api_courses.php');
            if (res.data.success) setCourses(res.data.data);
        } catch (e) { console.error("Errore fetch corsi", e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchCourses();
        if (isSuperAdmin) {
            axios.get('/api_tenants.php').then(res => setTenants(res.data.data));
        }
    }, [fetchCourses, isSuperAdmin]);

    // --- CRUD CORSI ---
    const handleOpenForm = (course = null) => {
        resetModals(); // Chiude eventuali altri modali
        if (course) {
            setFormData({ id: course.id, name: course.name, description: course.description, tenant_id: course.tenant_id });
        } else {
            setFormData({ id: null, name: '', description: '', tenant_id: isSuperAdmin ? '' : auth.tenant_id });
        }
        setModals(m => ({ ...m, form: true }));
    };

    const handleSaveCourse = async () => {
        setModals(m => ({ ...m, form: false, loading: true }));
        try {
            const res = await axios.post('/api_courses.php?action=save_course', formData);
            if (res.data.success) {
                setStatusMsg('Corso salvato correttamente!');
                setModals(m => ({ ...m, loading: false, success: true }));
                fetchCourses();
            }
        } catch (e) { 
            setModals(m => ({ ...m, loading: false, error: true }));
            setStatusMsg('Errore durante il salvataggio.');
        }
    };

    const confirmDelete = (course) => {
        resetModals();
        setCurrentCourse(course);
        setModals(m => ({ ...m, confirmDelete: true }));
    }

    const handleDelete = async () => {
        setModals(m => ({ ...m, confirmDelete: false, loading: true }));
        try {
            await axios.delete(`/api_courses.php?id=${currentCourse.id}`);
            setModals(m => ({ ...m, loading: false, success: true }));
            setStatusMsg('Corso eliminato.');
            fetchCourses();
        } catch (e) {
            setModals(m => ({ ...m, loading: false, error: true }));
            setStatusMsg('Errore eliminazione.');
        }
    };

    // --- CLASSI (N:N) ---
    const openAssignClasses = async (course) => {
        resetModals();
        setCurrentCourse(course);
        setSelectedItems([]);
        try {
            const res = await axios.get(`/api_courses_utils.php?action=get_available_classes&tenant_id=${course.tenant_id}&course_id=${course.id}`);
            setAvailableClasses(res.data.classes);
            setSelectedItems(res.data.assigned_ids || []);
            setModals(m => ({ ...m, assignClasses: true }));
        } catch (e) { console.error(e); }
    };

    const saveClassAssignment = async () => {
        setModals(m => ({ ...m, assignClasses: false, loading: true }));
        try {
            await axios.post('/api_courses_utils.php?action=assign_classes', {
                course_id: currentCourse.id,
                class_ids: selectedItems
            });
            setModals(m => ({ ...m, loading: false, success: true }));
            setStatusMsg('Classi aggiornate con successo.');
            fetchCourses();
        } catch (e) { 
            setModals(m => ({ ...m, loading: false, error: true }));
            setStatusMsg('Errore salvataggio classi.');
        }
    };

    // --- DOCENTI & PROMOZIONE ---
    const openInstructorModal = async (course) => {
        resetModals();
        setCurrentCourse(course);
        setSelectedItems([]);
        try {
            const res = await axios.get(`/api_courses_utils.php?action=get_instructors&tenant_id=${course.tenant_id}&course_id=${course.id}`);
            setAvailableUsers(res.data.users);
            setSelectedItems(res.data.manager_ids || []);
            setModals(m => ({ ...m, assignInstructors: true }));
        } catch (e) { console.error(e); }
    };

    const promoteUser = async (userId) => {
        if(!window.confirm("Confermi di voler promuovere questo utente a Instructor?")) return;
        try {
            await axios.post('/api_courses.php?action=promote_user', { user_id: userId });
            // Ricarica la lista utenti per vedere il nuovo ruolo
            const res = await axios.get(`/api_courses_utils.php?action=get_instructors&tenant_id=${currentCourse.tenant_id}&course_id=${currentCourse.id}`);
            setAvailableUsers(res.data.users);
        } catch (e) { alert("Errore promozione"); }
    };

    const saveInstructors = async () => {
        setModals(m => ({ ...m, assignInstructors: false, loading: true }));
        try {
            await axios.post('/api_courses_utils.php?action=assign_instructors', {
                course_id: currentCourse.id,
                user_ids: selectedItems
            });
            setModals(m => ({ ...m, loading: false, success: true }));
            setStatusMsg('Docenti assegnati correttamente.');
            fetchCourses();
        } catch (e) { 
            setModals(m => ({ ...m, loading: false, error: true }));
            setStatusMsg('Errore salvataggio docenti.');
        }
    };

    return (
        <div className="container-fluid py-4 fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold text-primary m-0"><FaGraduationCap className="me-2"/> Gestione Corsi</h4>
                {(isSuperAdmin || isAdmin) && (
                    <button className="btn btn-primary rounded-pill px-4 shadow-sm" onClick={() => handleOpenForm()}>
                        <FaPlus className="me-2"/> Nuovo Corso
                    </button>
                )}
            </div>

            <div className="row">
                {loading ? <SkeletonLoader count={3} /> : courses.map(course => (
                    <div key={course.id} className="col-md-4 mb-4">
                        <div className="card shadow-sm border-0 h-100">
                            <div className="card-header bg-white border-0 pt-3 d-flex justify-content-between">
                                <h5 className="fw-bold text-dark m-0">{course.name}</h5>
                                <span className="badge bg-light text-primary border"><FaKey className="me-1"/> {course.enrollment_code}</span>
                            </div>
                            <div className="card-body">
                                <p className="text-muted small">{course.description}</p>
                                {isSuperAdmin && <div className="small mb-3 text-muted"><FaBuilding className="me-1"/> {course.tenant_name}</div>}
                                
                                <div className="d-flex gap-2 flex-wrap">
                                    {isInstructor ? (
                                        <button className="btn btn-primary btn-sm w-100 rounded-pill" onClick={() => navigate(`/classes?course_id=${course.id}`)}>
                                            <FaLink className="me-1"/> Gestisci Classi
                                        </button>
                                    ) : (
                                        <>
                                            <button className="btn btn-outline-primary btn-sm flex-grow-1 rounded-pill" onClick={() => openAssignClasses(course)}>
                                                <FaLink/> Classi ({course.classes_count})
                                            </button>
                                            <button className="btn btn-outline-secondary btn-sm flex-grow-1 rounded-pill" onClick={() => openInstructorModal(course)}>
                                                <FaUsersCog/> Docenti ({course.instructors_count})
                                            </button>
                                            <button className="btn btn-light btn-sm rounded-circle text-primary" onClick={() => handleOpenForm(course)}><FaEdit/></button>
                                            <button className="btn btn-light btn-sm rounded-circle text-danger" onClick={() => confirmDelete(course)}><FaTrash/></button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* --- MODALI --- */}
            
            <BaseModal show={modals.form} onClose={() => setModals(m => ({...m, form: false}))} title={formData.id ? 'Modifica Corso' : 'Nuovo Corso'}>
                <div className="text-start">
                    {isSuperAdmin && !formData.id && (
                        <div className="mb-3">
                            <label className="form-label small fw-bold">Tenant</label>
                            <select className="form-select" value={formData.tenant_id} onChange={e => setFormData({...formData, tenant_id: e.target.value})}>
                                <option value="">Seleziona...</option>
                                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Nome Corso</label>
                        <input type="text" className="form-control" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Descrizione</label>
                        <textarea className="form-control" rows="3" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}></textarea>
                    </div>
                    <div className="text-end">
                        <button className="btn btn-primary rounded-pill px-4" onClick={handleSaveCourse}>Salva</button>
                    </div>
                </div>
            </BaseModal>

            <BaseModal show={modals.assignClasses} onClose={() => setModals(m => ({...m, assignClasses: false}))} title="Collega Classi (N:N)" size="large">
                <div className="alert alert-info small text-start">Puoi collegare più classi allo stesso corso.</div>
                <div className="list-group text-start border" style={{maxHeight: '400px', overflowY: 'auto'}}>
                    {availableClasses.map(cls => (
                        <label key={cls.id} className="list-group-item d-flex align-items-center py-2">
                            <input className="form-check-input me-3" type="checkbox" 
                                checked={selectedItems.includes(cls.id)}
                                onChange={(e) => {
                                    if(e.target.checked) setSelectedItems([...selectedItems, cls.id]);
                                    else setSelectedItems(selectedItems.filter(id => id !== cls.id));
                                }}
                            />
                            <div className="fw-bold">{cls.name}</div>
                        </label>
                    ))}
                </div>
                <div className="mt-3 text-end">
                    <button className="btn btn-primary rounded-pill px-4" onClick={saveClassAssignment}>Salva</button>
                </div>
            </BaseModal>

            <BaseModal show={modals.assignInstructors} onClose={() => setModals(m => ({...m, assignInstructors: false}))} title="Gestione Docenti" size="large">
                <div className="list-group text-start border" style={{maxHeight: '400px', overflowY: 'auto'}}>
                    {availableUsers.map(user => (
                        <div key={user.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                            <div className="d-flex align-items-center">
                                <input className="form-check-input me-3" type="checkbox" 
                                    disabled={user.role === 'user'} 
                                    checked={selectedItems.includes(user.id)}
                                    onChange={(e) => {
                                        if(e.target.checked) setSelectedItems([...selectedItems, user.id]);
                                        else setSelectedItems(selectedItems.filter(id => id !== user.id));
                                    }} 
                                />
                                <div>
                                    <div className="fw-bold">{user.name} <span className={`badge ms-2 ${user.role==='instructor'?'bg-success':'bg-light text-dark border'}`}>{user.role}</span></div>
                                    <div className="small text-muted">{user.email}</div>
                                </div>
                            </div>
                            {user.role === 'user' && (
                                <button className="btn btn-sm btn-outline-warning" onClick={() => promoteUser(user.id)}>
                                    <FaUserPlus className="me-1"/> Promuovi
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-3 text-end">
                    <button className="btn btn-secondary rounded-pill px-4" onClick={saveInstructors}>Salva Docenti</button>
                </div>
            </BaseModal>

            <SuccessModal show={modals.success} message={statusMsg} onClose={() => setModals(m => ({...m, success: false}))} />
            <ErrorModal show={modals.error} message={statusMsg} onClose={() => setModals(m => ({...m, error: false}))} />
            <ConfirmModal show={modals.confirmDelete} title="Conferma Eliminazione" message="Eliminare questo corso?" onConfirm={handleDelete} onClose={() => setModals(m => ({...m, confirmDelete: false}))} isDestructive={true} />
            <LoadingModal show={modals.loading} message="Elaborazione..." />
        </div>
    );
};

export default CoursesModule;